// Pure helpers for the vol-surface DVOL column. DVOL is Deribit's implied-vol
// index, computed backend-side via `market_DVOL_calc` and only DISPLAYED here —
// no methodology is reconstructed in the FE. Everything in this module is pure
// and total: on any malformed input it returns null / {} rather than throwing,
// so a DVOL failure can never propagate into the surface table (IM-dvol).
import Utils from '../../../../utils/immersive_utils';
import { SnapshotTypeEnum } from '../../../../model/snapshot_type';

// DVOL only exists for BTC and ETH on Deribit. Any other token -> no request,
// blank column.
export const DVOL_SUPPORTED_SYMBOLS = ['BTC', 'ETH'] as const;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Deribit settles at 08:00 UTC and DVOL tenors are whole days added to the
// reference, so the reference MUST be pinned to the most recent 08:00:00 UTC
// at or before the displayed timestamp or every tenor is silently off by the
// intraday offset. Returns null for unparseable input.
export const pinTo0800Utc = (input: string | Date | null | undefined): Date | null => {
  if (input == null) return null;
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return null;
  const candidate = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 8, 0, 0, 0);
  // If the displayed instant is before 08:00 UTC that day, step back a day.
  const pinned = candidate <= d.getTime() ? candidate : candidate - MS_PER_DAY;
  return new Date(pinned);
};

// token -> DVOL symbol. The surface token can be a bare ticker ('BTC') or a
// base_quote wire id ('SOL_USDC'); the DVOL symbol is just the uppercased base.
// Returns null when the token is not a DVOL-supported symbol.
export const resolveDvolSymbol = (token: string | null | undefined): string | null => {
  if (!token) return null;
  const base = token.split('_', 1)[0]?.toUpperCase();
  if (!base) return null;
  return (DVOL_SUPPORTED_SYMBOLS as readonly string[]).includes(base) ? base : null;
};

// CM constant-maturity label ('1W'/'2W'/'1M'/'3M'/'6M'/'9M'/'2D'/'2Y'...) ->
// integer days. Uses the same unit convention the surface already sorts by
// (W=7, M=30, Y=365, D=1). Approximate by construction — DVOL expects integer
// days and constant-maturity tenors have no exact calendar anchor. Returns null
// for an unparseable label.
export const tenorLabelToDays = (label: string | null | undefined): number | null => {
  if (!label) return null;
  const m = /^(\d+)\s*([DWMY])$/i.exec(label.trim());
  if (!m) return null;
  const n = parseInt(m[1], 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  switch (m[2].toUpperCase()) {
    case 'D':
      return n;
    case 'W':
      return n * 7;
    case 'M':
      return n * 30;
    case 'Y':
      return n * 365;
    default:
      return null;
  }
};

// EE listed-expiry key -> integer days from the pinned reference. Keys are ISO
// dates ('YYYY-MM-DD'); DDMMMYY ('27DEC26') is accepted as a fallback in case a
// different surface source labels rows that way. The expiry is itself pinned to
// 08:00 UTC (Deribit settle) so the day count is exact. Returns null for an
// unparseable key or a non-positive day count.
export const expiryKeyToDays = (
  key: string | null | undefined,
  pinnedRef: Date | null | undefined,
): number | null => {
  if (!key || !pinnedRef || isNaN(pinnedRef.getTime())) return null;

  let expiryMs: number | null = null;

  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key.trim());
  if (iso) {
    expiryMs = Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]), 8, 0, 0, 0);
  } else {
    // parseDate matches month abbreviations case-sensitively (JAN..DEC), so
    // normalise before the fallback — a surface may label rows '27dec26'.
    const parsed = Utils.parseDate(key.trim().toUpperCase());
    if (parsed && !isNaN(parsed.getTime())) {
      expiryMs = Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), 8, 0, 0, 0);
    }
  }

  if (expiryMs == null) return null;
  const days = Math.floor((expiryMs - pinnedRef.getTime()) / MS_PER_DAY);
  return days > 0 ? days : null;
};

// Map a table row's tenor key to integer days, dispatching on the EE/CM toggle.
export const rowTenorToDays = (
  rowTenor: string | null | undefined,
  snapshotType: SnapshotTypeEnum,
  pinnedRef: Date | null | undefined,
): number | null =>
  snapshotType === SnapshotTypeEnum.EE
    ? expiryKeyToDays(rowTenor, pinnedRef)
    : tenorLabelToDays(rowTenor);

// Build the tenor list to send: dedupe, keep positive integers, sort ascending.
// The IM-2602 trap: `calc_for_tenor` needs a listed expiry strictly greater than
// the target to bracket it, so the LONGEST listed expiry can't be calculated and
// its presence fails the ENTIRE request (-32603, all tenors empty). In Expiry
// (EE) mode the rows ARE the listed expiries, so the largest MUST be dropped
// before sending (that row's cell then blanks). In Tenor (CM) mode the rows are
// constant-maturity points well inside the listed ladder, so nothing is dropped.
export const buildRequestTenors = (days: number[], dropLargest: boolean): number[] => {
  const clean = Array.from(new Set(days.filter((d) => Number.isInteger(d) && d > 0))).sort(
    (a, b) => a - b,
  );
  if (dropLargest && clean.length > 0) clean.pop();
  return clean;
};

// Parse a `market_DVOL_calc` response into a numeric-keyed map for one symbol.
// Tenor keys come back as STRINGS and values are vol POINTS already (36.96 means
// 36.96%). Partial responses are honoured: only finite values are kept, so a
// response that omits some tenors simply yields fewer entries — never an error.
export const parseDvolResponse = (resp: unknown, symbol: string): Record<number, number> => {
  const out: Record<number, number> = {};
  try {
    const rec = (v: unknown): Record<string, unknown> | undefined =>
      v && typeof v === 'object' ? (v as Record<string, unknown>) : undefined;
    // Accept either the bare result object or a JSON-RPC envelope.
    const root = rec(resp);
    const body = rec(root?.result) ?? root;
    const dvol = rec(rec(body)?.dvol);
    const perSymbol = rec(dvol?.[symbol]);
    if (!perSymbol) return out;
    for (const [tenorKey, value] of Object.entries(perSymbol)) {
      const days = parseInt(tenorKey, 10);
      const num = typeof value === 'number' ? value : Number(value);
      if (Number.isInteger(days) && Number.isFinite(num)) {
        out[days] = num;
      }
    }
  } catch {
    return {};
  }
  return out;
};

// Format a vol-points value for the cell. DVOL values are ALREADY percentages,
// so unlike the surface's percentFormat this must NOT multiply by 100.
export const formatDvol = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '';
  return `${Number(value).toFixed(2)}%`;
};
